(ns app.front.requirement.components
  (:require [app.front.process :as process]
            [app.front.requirement.events :as req-events]
            [app.front.requirement.subs :as req-subs]
            [app.front.ui.inline-editable :as inline-editable]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.placeholder-button :as ui-placeholder-button]
            [app.front.user :as front-user]
            [app.front.util :as futil]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-db ::open-add-requirement-popup
  (fn [db _]
    (assoc db ::add-requirement-popup-open? true)))

(rf/reg-event-db ::close-add-requirement-popup
  (fn [db _]
    (assoc db ::add-requirement-popup-open? false)))

(rf/reg-event-db ::toggle-add-requirement-popup
  (fn [db _]
    (update db ::add-requirement-popup-open? not)))

(rf/reg-sub ::add-requirement-popup-open?
  (fn [db _]
    (get db ::add-requirement-popup-open? false)))

(rf/reg-event-db ::reset-ui-state
  (fn [db _]
    (dissoc db ::add-requirement-popup-open?)))

(rf/reg-event-db ::toggle-bluebeam-dont-ask-again
  (fn [db _]
    (update db ::bluebeam-dont-ask-again? not)))

(rf/reg-event-db ::reset-bluebeam-dont-ask-again
  (fn [db _]
    (assoc db ::bluebeam-dont-ask-again? false)))

(rf/reg-sub ::bluebeam-dont-ask-again?
  (fn [db _]
    (get db ::bluebeam-dont-ask-again? false)))

(defn add-attachment-requirement-controls
  [{:keys [popup-open? add-loading? tr attachment-types target-id placeholder-button?]}]
  [:div.relative
   (if placeholder-button?
     [ui-placeholder-button/placeholder-button
      {:title         (tr [(if add-loading? :adding :attachments/add-attachment-type)])
       :icon          i/plus
       :icon-leading? true
       :circled-icon? true
       :uppercase?    false
       :disabled?     add-loading?
       :data-cy       "wizard-add-attachment-requirement"
       :class         "min-h-12 text-sm"
       :on-click      #(>evt [::toggle-add-requirement-popup])}]
     [:button.w-full.h-full.px-2.py-4.flex.justify-center.items-center.bg-transparent.border-dashed.border-ui-dark-gray.border.text-ui-violet-100.uppercase.font-bold.text-xs.text-center.disabled:opacity-50
      {:type     "button"
       :on-click #(>evt [::toggle-add-requirement-popup])
       :disabled add-loading?}
      (if-not add-loading?
        (str (tr [:attachments/add-attachment-type]) " +")
        (str (tr [:adding])))])
   (when (and (not add-loading?) popup-open?)
     [inline-editable/popup-wrapper
      {:tr      tr
       :title   :attachments/add-attachment-type
       :data-cy "add-attachment-requirement"}
      (if (<sub [::ws-subs/loading-config?])
        (cc/select-skeleton {:hide-label true})
        [inline-editable/select-field-confirm
         {:tr           tr
          :select-opts  {:id         "new-attachment-type"
                         :light      true
                         :label-text (tr [:attachments/add-attachment-type])
                         :hide-label true}
          :items        (->> attachment-types
                             (concat [{:text  (tr [:select-option])
                                       :value ""}]))
          :allow-nil?   false
          :on-confirm   #(do (>evt [::close-add-requirement-popup])
                             (>evt [::req-events/add-attachment-requirement target-id (futil/parse-int %) nil]))
          :on-cancel    #(>evt [::close-add-requirement-popup])
          :confirm-opts {:class-name "border-0"
                         :data-cy    "confirm-add-attachment-requirement"
                         :text       (tr [:attachments/add])}
          :cancel-opts  {:class-name "border-0"
                         :text       (tr [:cancel])}}])])])

(defn- attachment-requirement [{:requirement/keys [title border-color attachment-count attachment-type]
                                :db/keys          [id]}
                               {:keys [can-remove-requirements? remove-loading? target-id tr]}]
  (let [loading-config? (<sub [::ws-subs/loading-config?])]
    [:div.attachment-requirement.bg-ui-light-gray.p-3.border-solid.border-l-4.flex.justify-between.items-center.leading-tight.relative.overflow-hidden
     {:key   id
      :class border-color}
     [:div.mr-2.font-bold.w-full
      (if loading-config?
        (cc/skeleton-text)
        title)]
     [:div.px-3.py-1.font-bold.bg-ui-night-sky-100.text-ui-white attachment-count]
     (when (and can-remove-requirements? (not loading-config?))
       [:div.remove-overlay.absolute.top-0.right-0.bottom-0.justify-center.items-center
        (cc/button {:size       :small
                    :disabled   remove-loading?
                    :class-name "relative"
                    :on-click   #(>evt [::req-events/remove-attachment-requirement target-id attachment-type])}
                   (tr [:remove]))])]))

(defn attachment-requirements-list [tr target-id]
  (let [add-loading?             (<sub [::req-subs/add-loading?])
        requirements             (<sub [::req-subs/all-attachment-requirements target-id])
        show-requirements-list?  (<sub [::req-subs/show-requirements-list? target-id])
        remove-loading?          (<sub [::req-subs/remove-loading?])
        attachment-types         (<sub [::req-subs/attachment-types requirements])
        can-add-requirements?    (<sub [::req-subs/can-add-requirements? target-id])
        can-remove-requirements? (<sub [::process/signal-permitted? target-id [:process.signal/remove-attachment-requirement]])
        popup-open?              (<sub [::add-requirement-popup-open?])]
    (when show-requirements-list?
      [:div.relative
       [:div.mb-3>strong (tr [:required-attachments])]
       [:div.grid.sm:grid-cols-2.lg:grid-cols-3.xl:grid-cols-4.gap-3
        (when can-add-requirements?
          [add-attachment-requirement-controls
           {:popup-open?      popup-open?
            :add-loading?     add-loading?
            :tr               tr
            :attachment-types attachment-types
            :target-id        target-id}])
        (futil/for-frag [requirement requirements]
          [attachment-requirement requirement (util/mmap can-remove-requirements? remove-loading? target-id tr)])]])))

(defn- save-bluebeam-preference! [prefer-desktop?]
  (let [enable-flag  (if prefer-desktop? :user.config-flag/prefers-bluebeam-desktop? :user.config-flag/prefers-bluebeam-browser?)
        disable-flag (if prefer-desktop? :user.config-flag/prefers-bluebeam-browser? :user.config-flag/prefers-bluebeam-desktop?)]
    (>evt [::front-user/set-config-flag enable-flag true])
    (>evt [::front-user/set-config-flag disable-flag false])))

(defn- session-modal-content [tr on-close on-browser-click on-studio-click on-close-fn dont-ask-again?]
  [ui-modal/composed-input-modal
   {:open                   true
    :selector-primary-focus "button.bx--modal-close"
    :on-close               (on-close-fn on-close)}
   (cc/modal-header
     {:close-modal on-close
      :title       (tr [:review/open-external-session-title])})
   (cc/modal-body
     {:class-name "mb-0 pb-2"}
     [:div.py-2 (tr [:review/open-external-session-body])]
     (cc/checkbox {:id         "review-integration-session-dont-ask"
                   :label-text (tr [:review/dont-ask-again])
                   :checked    dont-ask-again?
                   :on-change  #(>evt [::toggle-bluebeam-dont-ask-again])}))
   (cc/modal-footer
     {}
     (cc/button
       {:kind     :secondary
        :data-cy  "review-integration-session-browser"
        :on-click on-browser-click}
       (tr [:review/open-in-browser]))
     (cc/button
       {:kind     :primary
        :data-cy  "review-integration-session-studio"
        :on-click on-studio-click}
       (tr [:review/open-in-bluebeam-revu])))])

(defn review-integration-session-modal [tr]
  (let [{:keys [session-id web-url studio-url]} (<sub [::req-subs/review-session-confirmation])
        config-flags                            (-> (<sub [:session/user]) :user/config-flags set)
        prefers-browser?                        (contains? config-flags :user.config-flag/prefers-bluebeam-browser?)
        prefers-desktop?                        (contains? config-flags :user.config-flag/prefers-bluebeam-desktop?)
        has-preference?                         (or prefers-browser? prefers-desktop?)
        dont-ask-again?                         (<sub [::bluebeam-dont-ask-again?])]
    (when (and session-id has-preference?)
      ;; Auto-open when user has preference set
      (let [url (if prefers-desktop? studio-url web-url)]
        (futil/open-in-new-tab-or-window url)
        (>evt [::req-events/clear-session-confirmation])
        (>evt [::reset-bluebeam-dont-ask-again]))
      nil)
    (when (and session-id (not has-preference?))
      ;; Show modal when user hasn't set a preference yet
      (let [on-close         #(do
                                (>evt [::req-events/clear-session-confirmation])
                                (>evt [::reset-bluebeam-dont-ask-again]))
            open-in-browser  #(do
                                (on-close)
                                (futil/open-in-new-tab-or-window web-url))
            open-in-studio   #(do
                                (on-close)
                                (futil/open-in-new-tab-or-window studio-url))
            on-browser-click #(do
                                (when dont-ask-again?
                                  (save-bluebeam-preference! false))
                                (open-in-browser))
            on-studio-click  #(do
                                (when dont-ask-again?
                                  (save-bluebeam-preference! true))
                                (open-in-studio))
            on-close-fn      (futil/make-callback-fn
                               (fn [on-close _] (on-close) true))]
        [session-modal-content tr on-close on-browser-click on-studio-click on-close-fn dont-ask-again?]))))
